import { axiosFetch, isAxiosResponseError } from '@halo/_utils/axios'
import { IApiCallOptions, IApiListResponse, IApiResponse } from '@pushly/aqe/lib/interfaces'

export abstract class AbstractEntityService<T> {
    /**
     * Adds additional query params found in opts to the provided url.
     */
    protected static buildUrl(url: string, opts: IApiCallOptions = {}) {
        const u = new URL(url)

        if (opts.query) {
            for (const key in opts.query) {
                if (opts.query.hasOwnProperty(key)) {
                    const value = opts.query[key]
                    if (value !== undefined) {
                        u.searchParams.append(key, opts.query[key])
                    }
                }
            }
        }

        return u.toString()
    }

    protected async execFetchMany(url: string, opts: IApiCallOptions = {}): Promise<IApiListResponse<T>> {
        try {
            const builtUrl = AbstractEntityService.buildUrl(url, opts)
            const req = await axiosFetch(
                'get',
                {
                    url: builtUrl,
                },
                opts.cancellationKey,
            )

            return req.data
        } catch (error) {
            if (isAxiosResponseError(error)) {
                throw error
            }
        }
    }

    protected async execFetchOne(url: string, opts: IApiCallOptions = {}): Promise<IApiResponse<T>> {
        let ok = false
        let item: T

        try {
            const builtUrl = AbstractEntityService.buildUrl(url, opts)
            const req = await axiosFetch(
                'get',
                {
                    url: builtUrl,
                },
                opts.cancellationKey,
            )

            ok = true
            item = req.data.data
        } catch (error) {
            if (isAxiosResponseError(error)) {
                throw error
            }
        }

        return { ok, data: item! }
    }

    protected async execCreate(
        url: string,
        createDto: Partial<T>,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<T>> {
        let ok = false
        let item: T

        try {
            const builtUrl = AbstractEntityService.buildUrl(url, opts)
            const req = await axiosFetch(
                'post',
                {
                    url: builtUrl,
                    data: createDto,
                },
                opts.cancellationKey,
            )

            ok = true
            item = req.data.data
        } catch (error) {
            if (isAxiosResponseError(error)) {
                throw error
            }
        }

        return { ok, data: item! }
    }

    protected async execPatch(
        url: string,
        updateDto: Partial<T>,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<T>> {
        let ok = false
        let item: T

        try {
            const builtUrl = AbstractEntityService.buildUrl(url, opts)
            const req = await axiosFetch(
                'patch',
                {
                    url: builtUrl,
                    data: updateDto,
                },
                opts.cancellationKey,
            )

            ok = true
            item = req.data.data
        } catch (error) {
            if (isAxiosResponseError(error)) {
                throw error
            }
        }

        return { ok, data: item! }
    }

    protected async execDelete(url: string, opts: IApiCallOptions = {}): Promise<IApiResponse<string>> {
        let ok = true

        try {
            const builtUrl = AbstractEntityService.buildUrl(url, opts)
            await axiosFetch(
                'delete',
                {
                    url: builtUrl,
                },
                opts.cancellationKey,
            )
        } catch (error) {
            if (isAxiosResponseError(error)) {
                throw error
            }
        }

        return { ok, data: 'success' }
    }
}
